import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, CreditCard, Building2, Save, Calendar, CheckCircle2, Clock, Star } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import SelectInput from '../../components/SelectInput';
import BookingQrCard from '../../components/BookingQrCard';
import ClinicPicker from '../../components/ClinicPicker';
import ClinicDetailsCard from '../../components/ClinicDetailsCard';
import { useAuth } from '../../hooks/useAuth';
import EmailPromptModal from '../../components/EmailPromptModal';
import { ROLES } from '../../utils/roles';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDate, formatDateTime } from '../../utils/format';
import * as ordersApi from '../../api/orders.api';
import * as vendorApi from '../../api/vendor.api';
import * as paymentsApi from '../../api/payments.api';
import * as usersApi from '../../api/users.api';
import * as bookingsApi from '../../api/bookings.api';
import * as reviewsApi from '../../api/reviews.api';
import { trackConsultationCompletedPatient } from '../../utils/analytics';

function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const role = user?.role;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Admin assign-vendor state
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Vendor status update state
  const [statusVal, setStatusVal] = useState('');
  const [updating, setUpdating] = useState(false);

  // User pay state
  const [paying, setPaying] = useState(false);
  // Payment attempts for this order — used to decide whether to show the
  // "Pay now" button. If there's an in-flight PENDING attempt we hide the
  // button (avoids the user paying twice while a webhook is on its way).
  const [orderPayments, setOrderPayments] = useState([]);

  // Phase 5 — schedule modal state. Client 2026-08-04: replaced the
  // free-text datetime picker with a date + slot-chip picker that pulls
  // the clinic's real availability from the backend.
  const [scheduleOpen, setScheduleOpen]     = useState(false);
  // Client 2026-09-09: when true, treat the assignedVendor as if it were
  // unset locally so the ClinicPicker reopens for the patient to switch.
  const [changingClinic, setChangingClinic] = useState(false);
  const [scheduleYmd, setScheduleYmd]       = useState(''); // "YYYY-MM-DD"
  const [selectedSlot, setSelectedSlot]     = useState(null); // { startAt, label }
  const [slotsList, setSlotsList]           = useState(null); // { date, closed, reason?, slots }
  const [slotsLoading, setSlotsLoading]     = useState(false);
  const [scheduling, setScheduling]         = useState(false);

  // Service delivery flow — client 2026-08-03: customer-only. The clinic
  // has no closing action; the customer's "Yes, I received the service"
  // click transitions the order all the way from ARRIVED/IN_PROGRESS
  // straight to COMPLETED, capturing the star rating + review in the
  // same step.
  const [confirming, setConfirming] = useState(false);

  // Patient confirmation → COMPLETED. Client 2026-08-03 asked for the
  // rating + review to be captured inline with confirmation so the order
  // isn't marked "complete" until the customer both confirms receipt AND
  // rates the clinic in the same step.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');

  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role);
  const isStaff = role === ROLES.STAFF;
  const isVendor = role === ROLES.VENDOR;
  const isPatient = role === ROLES.USER;

  async function load() {
    setLoading(true);
    try {
      // Use vendor endpoint for vendor; my-orders match for user; admin-orders for admin
      // Fallback to scanning my orders if direct fetch endpoint isn't role-specific.
      let found = null;
      if (isVendor) {
        found = await vendorApi.getMyOrder(id);
      } else if (isAdmin || isStaff) {
        // Direct lookup endpoint avoids the full-list scan workaround.
        found = await bookingsApi.getOrderById(id);
      } else {
        const mine = await ordersApi.myOrders();
        found = (mine || []).find((o) => o._id === id);
      }
      if (!found) throw new Error('Order not found');
      setOrder(found);
      setStatusVal(found.status);

      // For USER role, also pull the payment attempts for this order so
      // the Pay now button hides while a payment is still PENDING
      // confirmation. Silent on failure — button falls back to current
      // Order.paymentStatus logic if the fetch fails.
      if (isPatient) {
        try {
          const all = await paymentsApi.myPayments();
          const forThis = (all || []).filter((p) => {
            const pid = p.order?._id || p.order;
            return pid && String(pid) === String(found._id);
          });
          setOrderPayments(forThis);
        } catch { setOrderPayments([]); }
      }
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load vendors lazily for admin view
  useEffect(() => {
    if (isAdmin && order && order.paymentStatus === 'PAID' && !order.assignedVendor) {
      usersApi.listAdminUsers({ role: 'VENDOR', isActive: 'true' })
        .then((data) => setVendors(data || []))
        .catch(() => setVendors([]));
    }
  }, [isAdmin, order]);

  async function onAssignVendor() {
    if (!vendorId) { toast.error('Pick a vendor'); return; }
    setAssigning(true);
    try {
      await ordersApi.assignVendor(order._id, vendorId);
      toast.success('Vendor assigned');
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setAssigning(false);
    }
  }

  async function onUpdateStatus(explicitStatus) {
    // Accept a direct argument (used by the single-action "Service
    // delivered" button so it doesn't race the SelectInput's setState);
    // fall back to whatever the dropdown holds when called without args.
    const target = typeof explicitStatus === 'string' ? explicitStatus : statusVal;
    if (!['IN_PROGRESS', 'SERVICE_DELIVERED'].includes(target)) {
      toast.error('Clinics can set In progress or Service delivered — Completed is only reachable via the customer\'s confirmation.');
      return;
    }
    setUpdating(true);
    try {
      await vendorApi.updateOrderStatus(order._id, target);
      toast.success('Status updated');
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUpdating(false);
    }
  }

  async function onPay() {
    // Curlec requires customer.email — pause here and prompt if the
    // customer signed up with only a phone number.
    if (!user?.email) {
      setEmailModalOpen(true);
      return;
    }
    setPaying(true);
    try {
      const data = await paymentsApi.createPayment(order._id);
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        toast.success('Payment created');
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPaying(false);
    }
  }

  async function onEmailSaved(email) {
    // Client 2026-09-08: email is per-payment only, not saved to profile.
    setEmailModalOpen(false);
    setPaying(true);
    try {
      const data = await paymentsApi.createPayment(order._id, email);
      if (data.paymentUrl) window.location.href = data.paymentUrl;
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPaying(false);
    }
  }

  // Fetch the clinic's available slots for the selected day whenever the
  // patient changes the date in the schedule modal. Clears the current
  // slot pick so they explicitly choose one on the new day.
  useEffect(() => {
    if (!scheduleOpen || !scheduleYmd || !order?.assignedVendor) return;
    const clinicId = typeof order.assignedVendor === 'object'
      ? order.assignedVendor._id
      : order.assignedVendor;
    let cancelled = false;
    setSlotsLoading(true);
    setSelectedSlot(null);
    bookingsApi
      .getAvailableSlots(clinicId, scheduleYmd)
      .then((res) => { if (!cancelled) setSlotsList(res); })
      .catch((e) => { if (!cancelled) toast.error(errorMessage(e)); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [scheduleOpen, scheduleYmd, order?.assignedVendor]);

  async function onSchedule() {
    if (!selectedSlot) { toast.error('Pick an available time slot'); return; }
    setScheduling(true);
    try {
      await bookingsApi.scheduleBooking(order._id, selectedSlot.startAt, selectedSlot.label);
      toast.success('Booking scheduled');
      setScheduleOpen(false);
      setScheduleYmd('');
      setSelectedSlot(null);
      setSlotsList(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setScheduling(false);
    }
  }

  // Open the confirm+review modal. Prefill any existing draft review the
  // user may have started earlier for this booking so they don't lose it.
  async function openConfirmModal() {
    try {
      const existing = await reviewsApi.reviewForBooking(order._id).catch(() => null);
      if (existing) {
        setReviewRating(existing.rating || 0);
        setReviewComment(existing.comment || '');
      }
    } catch (_) { /* ignore — modal opens empty */ }
    setConfirmOpen(true);
  }

  // Submits the rating+review, then flips the order to COMPLETED. Client
  // 2026-08-03: confirmation and review happen in the same step — the
  // order is only "fully complete" once the customer both confirms
  // receipt and rates the clinic.
  async function onConfirmServiceWithReview() {
    if (!reviewRating) { toast.error('Please give a rating first.'); return; }
    setConfirming(true);
    try {
      await reviewsApi.submitReview(order._id, reviewRating, reviewComment.trim());
      await ordersApi.confirmService(order._id);
      // GA4 conversion event — patient confirmed the in-person clinic visit.
      trackConsultationCompletedPatient({ booking_id: order.bookingId || order.orderNumber });
      toast.success('Thanks! Your service is confirmed and your review is saved.');
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setConfirming(false);
    }
  }

  if (loading) return <LoadingSpinner size="lg" />;
  if (err) return <ErrorState message={err} action={<button className="btn" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Back</button>} />;
  if (!order) return null;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Orders', to: '/orders' }, { label: order.orderNumber }]}
        title={`Order ${order.orderNumber}`}
        subtitle={`Created ${formatDateTime(order.createdAt)}`}
        actions={
          <>
            {isPatient
              && order.paymentStatus !== 'PAID'
              && order.status !== 'CANCELLED'
              // Suppress the button while a payment attempt is still
              // in-flight (webhook not yet received). Show it again if
              // the previous attempt FAILED or was CANCELLED, or if no
              // attempt has been made yet.
              && !orderPayments.some((p) => p.status === 'PENDING')
              && (
              <button className="btn btn-primary" onClick={onPay} disabled={paying}>
                {paying ? <span className="spinner" /> : <CreditCard size={16} />}
                Pay now
              </button>
            )}
            {isPatient
              && order.paymentStatus !== 'PAID'
              && order.status !== 'CANCELLED'
              && orderPayments.some((p) => p.status === 'PENDING') && (
              <span className="badge amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} /> Finalising your last payment…
              </span>
            )}
            {/* Vendor header actions — client 2026-08-04: "Upload report"
                and "User progress" removed from this page (still reachable
                from the clinic dashboard). No closing action here either;
                the customer's "Yes, I received the service" click is what
                closes the order. */}
          </>
        }
      />

      {/* Client 2026-09-10: patients need to see WHICH clinic they're going
          to on the order details page — not just a name buried further down.
          Renders above everything else so the address / hours / Maps link
          are the first things they see when opening the order. Hidden while
          the patient is switching clinics (ClinicPicker takes over). */}
      {isPatient
        && order.assignedVendor
        && typeof order.assignedVendor === 'object'
        && !changingClinic
        && !['CANCELLED', 'COMPLETED'].includes(order.status) && (
        <ClinicDetailsCard
          clinic={order.assignedVendor}
          onChangeClinic={
            ['ARRIVED', 'IN_PROGRESS', 'SERVICE_DELIVERED'].includes(order.status)
              ? undefined
              : () => setChangingClinic(true)
          }
        />
      )}

      {/* Single confirmation CTA — client 2026-08-03: the clinic has no
          closing button. Once the patient has checked in, ONLY this
          button (opens the star + review popup) closes the order. */}
      {isPatient && ['ARRIVED', 'IN_PROGRESS', 'SERVICE_DELIVERED'].includes(order.status) && (
        <div className="notice info" style={{ marginBottom: 'var(--s-4)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Clock size={18} style={{ marginTop: 2, color: 'var(--brand-700)' }} />
          <div style={{ flex: 1 }}>
            <div className="bold">Did you receive your service?</div>
            <div className="text-sm muted" style={{ marginTop: 2 }}>
              Once you&apos;re done at the clinic, confirm to close the order and rate your experience.
            </div>
          </div>
          <button className="btn btn-primary" onClick={openConfirmModal} disabled={confirming}>
            {confirming ? <span className="spinner" /> : <CheckCircle2 size={16} />}
            Yes, I received the service
          </button>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <h3>Status</h3>
            <div className="row">
              <StatusBadge status={order.status} />
              <StatusBadge status={order.paymentStatus} />
            </div>
          </div>
          <dl className="kvs">
            <dt>Package</dt>
            <dd>
              <div className="cell-strong">{order.package?.name || order.packageSnapshot?.name}</div>
              {order.package?._id && <Link className="text-sm" to={`/packages/${order.package._id}`}>View package</Link>}
            </dd>
            <dt>Amount</dt><dd className="bold">{formatMoney(order.amount, order.currency || 'MYR')}</dd>
            <dt>Order #</dt><dd className="cell-mono">{order.orderNumber}</dd>
            <dt>Created</dt><dd>{formatDateTime(order.createdAt)}</dd>
            {order.assignedAt && <><dt>Assigned at</dt><dd>{formatDateTime(order.assignedAt)}</dd></>}
            {order.serviceDeliveredAt && (
              <>
                <dt>Service marked delivered</dt>
                <dd>
                  {formatDateTime(order.serviceDeliveredAt)}
                  {order.serviceDeliveryNote && (
                    <div className="text-xs muted" style={{ marginTop: 2 }}>
                      &ldquo;{order.serviceDeliveryNote}&rdquo;
                    </div>
                  )}
                </dd>
              </>
            )}
            {order.serviceConfirmedAt && (
              <>
                <dt>Confirmed by customer</dt>
                <dd>{formatDateTime(order.serviceConfirmedAt)}</dd>
              </>
            )}
            {order.completedAt && <><dt>Completed at</dt><dd>{formatDateTime(order.completedAt)}</dd></>}
            {order.notes && <><dt>Notes</dt><dd>{order.notes}</dd></>}
          </dl>
        </div>

        {(isVendor || isAdmin || isStaff) && (
          <div className="card">
            <div className="card-title">
              <h3>User</h3>
            </div>
            {/* Client 2026-08-28: the backend hides patient identifiers
                from the clinic until the patient physically checks in.
                A `_piiHidden` marker tells us to show a placeholder
                block instead of empty fields. Admin/staff always see
                full details. */}
            {order.user?._piiHidden ? (
              <div className="notice info" style={{ marginTop: 4 }}>
                <strong>Patient details hidden until check-in.</strong>
                <div className="muted text-sm" style={{ marginTop: 4 }}>
                  For patient privacy, contact and medical details unlock
                  in your portal once the patient checks in at the clinic.
                </div>
              </div>
            ) : order.user ? (
              <dl className="kvs">
                <dt>Name</dt><dd className="cell-strong">{order.user.name}</dd>
                <dt>Email</dt><dd>{order.user.email}</dd>
                {order.user.phone && <><dt>Phone</dt><dd>{order.user.phone}</dd></>}
                {order.user.medicalProfile && (
                  <>
                    <dt>Gender</dt><dd>{order.user.medicalProfile.gender || '—'}</dd>
                    <dt>DOB</dt><dd>{formatDate(order.user.medicalProfile.dateOfBirth) || '—'}</dd>
                    <dt>Blood group</dt><dd>{order.user.medicalProfile.bloodGroup || '—'}</dd>
                    <dt>Allergies</dt><dd>{(order.user.medicalProfile.allergies || []).join(', ') || '—'}</dd>
                    <dt>Conditions</dt><dd>{(order.user.medicalProfile.existingConditions || []).join(', ') || '—'}</dd>
                  </>
                )}
              </dl>
            ) : <div className="muted">User details unavailable.</div>}
          </div>
        )}
      </div>

      {/* Admin: assign vendor */}
      {isAdmin && order.paymentStatus === 'PAID' && !order.assignedVendor && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <div className="card-title"><h3>Assign a vendor / lab</h3></div>
          <div className="grid grid-2">
            <SelectInput
              label="Vendor"
              placeholder="Select a vendor…"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              options={vendors.map((v) => ({ value: v._id, label: `${v.name} · ${v.email}` }))}
            />
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <button className="btn btn-primary" onClick={onAssignVendor} disabled={assigning || !vendorId}>
                {assigning ? <span className="spinner" /> : <Building2 size={16} />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {(isAdmin || isStaff) && order.assignedVendor && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <div className="card-title"><h3>Assigned vendor</h3></div>
          <div className="muted">
            Assigned at {formatDateTime(order.assignedAt)}.
            {order.assignedVendor && (typeof order.assignedVendor === 'object' ? ` ${order.assignedVendor.name || ''}` : '')}
          </div>
        </div>
      )}

      {/* Admin/staff timeline — every recorded status transition with its
          timestamp. Client 2026-08-03: super-admin needs to see the full
          audit trail of a booking's progress at a glance.
          Client 2026-08-04: clinic (vendor) sees the same timeline too. */}
      {(isAdmin || isStaff || isVendor) && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <div className="card-title"><h3>Status timeline</h3></div>
          <StatusTimeline order={order} />
        </div>
      )}

      {/* Client 2026-08-06: "Mark balance paid" button removed from the
          clinic view — balance collection is handled outside the app.
          Admin can still reconcile via /admin/orders if ever needed. */}

      {/* Vendor: single-action button to mark the service delivered.
          Client 2026-08-04: patient must be checked in FIRST — button
          only appears once status is ARRIVED or IN_PROGRESS. Before
          that, we show a "waiting for check-in" notice so the clinic
          knows why the action isn't available. */}
      {isVendor && !['SERVICE_DELIVERED','COMPLETED','CANCELLED'].includes(order.status) && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <div className="card-title">
            <h3>Mark service delivered</h3>
          </div>
          {['ARRIVED', 'IN_PROGRESS'].includes(order.status) ? (
            <>
              <button
                className="btn btn-primary"
                onClick={() => onUpdateStatus('SERVICE_DELIVERED')}
                disabled={updating}
              >
                {updating ? <span className="spinner" /> : <Save size={16} />}
                Service delivered (awaiting customer confirm)
              </button>
              <div
                className="notice info"
                style={{
                  marginTop: 'var(--s-3)',
                  padding: '10px 12px',
                  borderLeft: '3px solid var(--brand-500, #14b8a6)',
                  background: 'var(--brand-50, #ecfeff)',
                  borderRadius: 6,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--ink-800, #1f2937)',
                }}
              >
                The customer will be notified to confirm receipt &amp; rate the clinic —
                <strong> only their confirmation fully completes the order.</strong>
              </div>
            </>
          ) : (
            <div
              className="notice warning"
              style={{
                padding: '10px 12px',
                borderLeft: '3px solid var(--amber-500, #f59e0b)',
                background: 'var(--amber-50, #fffbeb)',
                borderRadius: 6,
                fontSize: 13,
                lineHeight: 1.5,
                color: 'var(--ink-800, #1f2937)',
              }}
            >
              <strong>Patient hasn&apos;t checked in yet.</strong> Scan their QR /
              enter their AIH-XXXX code at check-in first — only then can you
              mark the service delivered.
            </div>
          )}
        </div>
      )}

      {/* QR / booking pass — visible once a date is picked. */}
      {isPatient && order.bookingId && <BookingQrCard order={order} />}

      {/* Patient view: progress + booking-flow CTAs */}
      {isPatient && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <div className="card-title"><h3>What happens next</h3></div>
          {order.status === 'PENDING_PAYMENT' && (
            <div className="muted">Complete deposit payment to start your booking.</div>
          )}
          {/* Client 2026-09-08: patients pick their own clinic from a
              list sorted by their current GPS location. Client 2026-09-09:
              they can also tap "Change clinic" on the details card to
              switch, which reopens the picker via `changingClinic`. */}
          {!isAdmin && !isStaff && order.paymentStatus === 'PAID'
            && (!order.assignedVendor || changingClinic)
            && !['ARRIVED', 'IN_PROGRESS', 'SERVICE_DELIVERED', 'CANCELLED', 'COMPLETED'].includes(order.status) && (
            <ClinicPicker
              orderId={order._id}
              onPicked={(updated) => { setOrder(updated); setChangingClinic(false); }}
            />
          )}
          {order.paymentStatus === 'PAID'
            && order.assignedVendor
            && !changingClinic
            && !order.scheduledAt
            && !['CANCELLED', 'COMPLETED'].includes(order.status) && (
            <div>
              <div className="muted" style={{ marginBottom: 12 }}>
                A clinic has been assigned to your booking. Pick a date and time for your first session.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => setScheduleOpen(true)}>
                  <Calendar size={16} /> Schedule appointment
                </button>
                {!isAdmin && !isStaff && (
                  <button className="btn btn-ghost" onClick={() => setChangingClinic(true)}>
                    Change clinic
                  </button>
                )}
              </div>
            </div>
          )}
          {order.status === 'BOOKED' && !changingClinic && (
            <div>
              <div className="muted" style={{ marginBottom: 12 }}>
                Your appointment is locked in. Show your booking pass at the clinic.
              </div>
              {!isAdmin && !isStaff && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline" onClick={() => setScheduleOpen(true)}>
                    <Calendar size={16} /> Reschedule
                  </button>
                  <button className="btn btn-ghost" onClick={() => setChangingClinic(true)}>
                    Change clinic
                  </button>
                </div>
              )}
            </div>
          )}
          {order.status === 'ARRIVED' && (
            <div className="muted">Checked in — your clinic doctor will see you shortly. Once you&apos;re done, tap <strong>Yes, I received the service</strong> above to close the order.</div>
          )}
          {order.status === 'IN_PROGRESS' && (
            <div className="muted">Your treatment is underway. When you&apos;re done, tap <strong>Yes, I received the service</strong> above to close the order and rate the clinic.</div>
          )}
          {order.status === 'SERVICE_DELIVERED' && (
            <div className="muted">Awaiting your confirmation that you received the service.</div>
          )}
          {order.status === 'COMPLETED' && (
            <div className="muted">Treatment cycle complete. Check My progress for outcomes.</div>
          )}
        </div>
      )}

      {/* Schedule modal — client 2026-08-04: date picker + slot chips.
          Slot chips come from the clinic's live availability
          (working days, hours, breaks, existing bookings). */}
      {scheduleOpen && (
        <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setScheduleOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true">
            <div className="modal-head"><h3>Schedule your appointment</h3></div>
            <div className="modal-body">
              <div className="muted text-sm" style={{ marginBottom: 12 }}>
                Pick a date. We&apos;ll show you the times your clinic is open.
              </div>
              <div className="field">
                <label>Date</label>
                <input
                  type="date"
                  className="input"
                  value={scheduleYmd}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setScheduleYmd(e.target.value)}
                />
              </div>

              {scheduleYmd && (
                <div className="field">
                  <label>Available times</label>
                  {slotsLoading && (
                    <div className="muted text-sm"><LoadingSpinner /> Loading slots…</div>
                  )}
                  {!slotsLoading && slotsList?.closed && (
                    <div className="notice warning" style={{ padding: '8px 12px', fontSize: 13 }}>
                      {slotsList.reason === 'not_a_working_day'
                        ? 'The clinic is closed on this day. Please pick a different date.'
                        : slotsList.reason === 'holiday'
                          ? 'This date is marked as a holiday by the clinic.'
                          : slotsList.reason === 'beyond_horizon'
                            ? 'Too far in the future — please pick an earlier date.'
                            : slotsList.reason === 'past_date'
                              ? 'That date is in the past.'
                              : 'No availability on this date.'}
                    </div>
                  )}
                  {!slotsLoading && !slotsList?.closed && (slotsList?.slots?.length ?? 0) === 0 && (
                    <div className="notice warning" style={{ padding: '8px 12px', fontSize: 13 }}>
                      All slots for this day are booked. Try another date.
                    </div>
                  )}
                  {!slotsLoading && !slotsList?.closed && (slotsList?.slots?.length ?? 0) > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {slotsList.slots.map((s) => {
                        const isPicked = selectedSlot?.startAt === s.startAt;
                        return (
                          <button
                            key={s.startAt}
                            type="button"
                            disabled={!s.available}
                            onClick={() => setSelectedSlot(s)}
                            className={isPicked ? 'btn btn-primary' : 'btn btn-outline'}
                            style={{
                              opacity: s.available ? 1 : 0.4,
                              cursor: s.available ? 'pointer' : 'not-allowed',
                              textDecoration: s.available ? 'none' : 'line-through',
                              padding: '6px 12px',
                              fontSize: 13,
                            }}
                            title={s.available ? 'Available' : 'Already booked'}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setScheduleOpen(false)} disabled={scheduling}>Cancel</button>
              <button className="btn btn-primary" onClick={onSchedule} disabled={scheduling || !selectedSlot}>
                {scheduling ? 'Scheduling…' : 'Confirm booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient: confirm service received + rate clinic (client 2026-08-03).
          The order only becomes COMPLETED once the customer both confirms
          receipt AND rates the clinic — the rating is required. */}
      {confirmOpen && (
        <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !confirming) setConfirmOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true">
            <div className="modal-head"><h3>Confirm service &amp; rate the clinic</h3></div>
            <div className="modal-body">
              <div className="muted text-sm" style={{ marginBottom: 12 }}>
                Confirming closes this order. Please rate the clinic — your feedback
                helps other patients pick the right clinic.
              </div>
              <div className="field">
                <label>Your rating <span className="text-xs" style={{ color: 'var(--rose-600)' }}>*</span></label>
                <div className="row" style={{ gap: 4 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setReviewRating(n)}
                      onMouseEnter={() => setReviewHover(n)}
                      onMouseLeave={() => setReviewHover(0)}
                      aria-label={`${n} star${n === 1 ? '' : 's'}`}
                      style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4 }}
                    >
                      <Star size={28} fill={(reviewHover || reviewRating) >= n ? '#f59e0b' : 'none'} stroke="#f59e0b" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>Comments <span className="muted text-xs">(optional)</span></label>
                <textarea
                  className="input"
                  rows={3}
                  maxLength={2000}
                  placeholder="What went well? Anything the clinic could improve?"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setConfirmOpen(false)} disabled={confirming}>Cancel</button>
              <button className="btn btn-primary" onClick={onConfirmServiceWithReview} disabled={confirming || !reviewRating}>
                {confirming ? 'Saving…' : 'Confirm & submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      <EmailPromptModal
        open={emailModalOpen}
        busy={paying}
        onSuccess={onEmailSaved}
        onCancel={() => setEmailModalOpen(false)}
      />
    </>
  );
}

// Format a millisecond duration as a compact "Xd Yh Zm" string. Drops
// zero-value units from the left so short spans read "12m" rather than
// "0d 0h 12m". Used at the bottom of the timeline for total lifecycle.
function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

// Renders every recorded lifecycle timestamp on the order in chronological
// order + a total-time summary. Steps with no timestamp are shown as
// "pending" so admins can spot where a booking is stuck without cross-
// referencing the audit log. Client 2026-08-04: needs to surface who
// placed the booking, the appointment date/time chosen, delivery, and
// completion — plus the total elapsed time from creation to completion.
function StatusTimeline({ order }) {
  const patientName =
       (order.user && typeof order.user === 'object' ? order.user.name : '')
    || order.customerName
    || '';
  const patientContact =
       (order.user && typeof order.user === 'object'
         ? (order.user.phone || order.user.email)
         : '')
    || '';

  const steps = [
    {
      key: 'createdAt',
      label: 'Order created by patient',
      at: order.createdAt,
      hint: [patientName, patientContact].filter(Boolean).join(' · '),
    },
    {
      key: 'paidAt',
      label: 'Payment received',
      at: order.paymentStatus === 'PAID' ? (order.updatedAt || order.createdAt) : null,
      hint: order.paymentStatus === 'PAID' ? '' : `payment ${order.paymentStatus}`,
    },
    { key: 'assignedAt', label: 'Clinic assigned', at: order.assignedAt },
    {
      key: 'scheduledAt',
      label: 'Appointment date & time',
      at: order.scheduledAt,
      // When missing, the row still renders with a "—" placeholder so
      // admins/clinics can tell at a glance that the patient hasn't
      // picked a slot yet.
      hint: order.scheduledSlot || '',
      valueOverride: order.scheduledAt ? formatDateTime(order.scheduledAt) : '—',
    },
    { key: 'arrivedAt', label: 'Patient checked in at clinic', at: order.arrivedAt },
    {
      key: 'serviceDeliveredAt',
      label: 'Clinic marked service delivered',
      at: order.serviceDeliveredAt,
      hint: order.serviceDeliveryNote || '',
    },
    { key: 'serviceConfirmedAt', label: 'Patient confirmed & rated', at: order.serviceConfirmedAt },
    { key: 'completedAt',        label: 'Order fully completed',     at: order.completedAt },
    {
      key: 'cancelledAt',
      label: 'Order cancelled',
      at: order.cancelledAt,
      hint: order.cancelReason || '',
    },
  ].filter((s) => s.at || ['assignedAt','scheduledAt','arrivedAt','serviceDeliveredAt','serviceConfirmedAt','completedAt'].includes(s.key));

  // Total elapsed = created → completed (falls back to cancelled if
  // applicable, or "in progress" so far if neither is set yet).
  const start = order.createdAt ? new Date(order.createdAt).getTime() : null;
  const endAt = order.completedAt || order.cancelledAt || null;
  const end   = endAt ? new Date(endAt).getTime() : Date.now();
  const totalMs = start ? Math.max(0, end - start) : null;
  const totalLabel = order.completedAt
    ? 'Total time (created → completed)'
    : order.cancelledAt
      ? 'Total time (created → cancelled)'
      : 'Elapsed so far (created → now)';

  return (
    <>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {steps.map((s) => (
          <li key={s.key} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 12, alignItems: 'flex-start' }}>
            <div
              aria-hidden="true"
              style={{
                width: 12, height: 12, borderRadius: '50%', marginTop: 6,
                background: s.at ? 'var(--brand-500, #14b8a6)' : 'var(--ink-200, #e5e7eb)',
                border: s.at ? 'none' : '1px dashed var(--ink-400, #9ca3af)',
              }}
            />
            <div>
              <div className={s.at ? 'bold' : 'muted'}>{s.label}</div>
              <div className="text-xs muted" style={{ marginTop: 2 }}>
                {s.valueOverride
                  ? s.valueOverride
                  : (s.at ? formatDateTime(s.at) : 'pending')}
                {s.hint && <> — {s.hint}</>}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {start !== null && (
        <div
          style={{
            marginTop: 'var(--s-4)',
            paddingTop: 'var(--s-3)',
            borderTop: '1px solid var(--border, #e5e7eb)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
          }}
        >
          <span className="muted text-sm">{totalLabel}</span>
          <span className="bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(totalMs)}</span>
        </div>
      )}
    </>
  );
}

export default OrderDetails;
